import React, { useState, useEffect, useMemo, useCallback } from "react";
import ReactEcharts from "echarts-for-react";
import * as echarts from "echarts";

import {
  Container,
  FilterBar,
  YearSelector,
  ChartsGrid,
  ChartCard,
  ChartTitle,
  ChartWrapper,
  EmptyState,
  ErrorMessage,
  SummaryRow,
  SummaryCard,
  ButtonGroup,
} from "./styles";

import Menu from "../../components/UI/Menu";
import Banner from "../../components/UI/Banner";
import ContentBox from "../../components/UI/ContentBox";
import Footer from "../../components/UI/Footer";
import FullScreenLoading from "../../components/UI/FullScreenLoading";
import ReadMoreButton from "../../components/UI/ReadMoreButton";

import useApi from "../../hooks/useApi";
import { images } from "../../constants";

const AVAILABLE_YEARS = [2022, 2023, 2024, 2025, 2026];

const COLORS = [
  "#4A90D9", "#50C878", "#FF6B6B", "#FFD93D", "#6C5CE7",
  "#FD79A8", "#00CEC9", "#E17055", "#0984E3", "#00B894",
  "#FDCB6E", "#E84393",
];

// ================================================================
// Safe helpers
// ================================================================
const safeArray = (arr) => (Array.isArray(arr) ? arr : []);
const safeNum = (val) => (typeof val === "number" ? val : 0);

// ================================================================
// Flatten all courses from courseType array
// ================================================================
const flattenAllCourses = (courseTypes) => {
  const result = [];
  safeArray(courseTypes).forEach((ct) => {
    if (!ct) return;
    safeArray(ct.coursesByCourseType).forEach((course) => {
      if (course) {
        result.push({
          ...course,
          _courseTypeName: ct.courseTypeName || "",
          _segmentName: null,
          _courseTypeId: ct.courseTypeId,
        });
      }
    });
    safeArray(ct.segment).forEach((seg) => {
      if (!seg) return;
      safeArray(seg.coursesBySegment).forEach((course) => {
        if (course) {
          result.push({
            ...course,
            _courseTypeName: ct.courseTypeName || "",
            _segmentName: seg.segmentName || "",
            _courseTypeId: ct.courseTypeId,
            _segmentId: seg.segmentId,
          });
        }
      });
    });
  });
  return result;
};

// ================================================================
// Aggregate courses by key
// ================================================================
const aggregateBy = (courses, keyFn, fields = ["subscriptionsLimit", "subscriptions", "graduates"]) => {
  const map = {};
  courses.forEach((c) => {
    const key = keyFn(c);
    if (!map[key]) {
      const obj = { key, count: 0, _ref: c };
      fields.forEach((f) => { obj[f] = 0; });
      map[key] = obj;
    }
    map[key].count += 1;
    fields.forEach((f) => { map[key][f] += safeNum(c[f]); });
  });
  return Object.values(map);
};

// ================================================================
// Merge profiles/grades
// ================================================================
const mergeProfiles = (courses) => {
  const map = {};
  courses.forEach((c) => {
    safeArray(c.subscriptionsByProfile).forEach((p) => {
      if (!p || !p.profileName) return;
      if (!map[p.profileName]) map[p.profileName] = { subscriptions: 0, graduates: 0 };
      map[p.profileName].subscriptions += safeNum(p.subscriptions);
      map[p.profileName].graduates += safeNum(p.graduates);
    });
  });
  return map;
};

const mergeGrades = (courses) => {
  const map = {};
  courses.forEach((c) => {
    safeArray(c.subscriptionsBySchoolGrade).forEach((g) => {
      if (!g || !g.schoolGrade) return;
      if (!map[g.schoolGrade]) map[g.schoolGrade] = { subscriptions: 0, graduates: 0 };
      map[g.schoolGrade].subscriptions += safeNum(g.subscriptions);
      map[g.schoolGrade].graduates += safeNum(g.graduates);
    });
  });
  return map;
};

// ================================================================
// Truncate chart labels
// ================================================================
const truncLabel = (str, max = 22) => {
  if (!str) return "";
  return str.length > max ? str.slice(0, max - 2) + "..." : str;
};

// ================================================================
// MAIN COMPONENT
// ================================================================
const CourseDashboard = () => {
  const [selectedYear, setSelectedYear] = useState(2025);
  const [dashboardData, setDashboardData] = useState(null);
  const [fullscreenChart, setFullscreenChart] = useState(null);

  // ---- Drill-down navigation state ----
  const [drill, setDrill] = useState({
    level: "overview", // "overview" | "courseType" | "segment"
    courseTypeName: null,
    courseTypeId: null,
    segmentName: null,
    segmentId: null,
    history: [], // stack of previous drill states for "back"
  });

  const [loadDashboard, loadDashboardInfo] = useApi({
    debounceDelay: 0,
    baseURL: process.env.REACT_APP_API_URL,
    url: "canalPedagogico/course/statistics/dashboards",
    method: "get",
    onCompleted: (response) => {
      if (!response.error) {
        console.log("Dashboard API Response:", response.data);
        setDashboardData(response.data);
      }
    },
    onError: (error) => {console.log("error",error);},
  });

  useEffect(() => {
    loadDashboard({ params: { year: selectedYear } });
    // eslint-disable-next-line
  }, [selectedYear]);

  // Reset navigation when year changes
  useEffect(() => {
    setDrill({ level: "overview", courseTypeName: null, courseTypeId: null, segmentName: null, segmentId: null, history: [] });
  }, [selectedYear]);

  const handleYearChange = (e) => {
    setSelectedYear(Number(e.target.value));
    setDashboardData(null);
  };

  // ================================================================
  // Extract coursesStatistics
  // ================================================================
  const statistics = useMemo(() => {
    if (!dashboardData) return null;
    if (Array.isArray(dashboardData) && dashboardData.length > 0) {
      return dashboardData[0]?.coursesStatistics || null;
    }
    return null;
  }, [dashboardData]);

  const courseTypesRaw = useMemo(() => safeArray(statistics?.courseType), [statistics]);

  // All courses flattened
  const allCourses = useMemo(() => flattenAllCourses(courseTypesRaw), [courseTypesRaw]);

  // ================================================================
  // Filtered courses based on drill level
  // ================================================================
  const filteredCourses = useMemo(() => {
    if (drill.level === "overview") {
      return allCourses; // all courses
    }
    if (drill.level === "courseType") {
      return allCourses.filter((c) => c._courseTypeName === drill.courseTypeName);
    }
    if (drill.level === "segment") {
      return allCourses.filter(
        (c) => c._courseTypeName === drill.courseTypeName && c._segmentName === drill.segmentName
      );
    }
    return allCourses;
  }, [allCourses, drill]);

  // ================================================================
  // Summary cards (based on filtered courses)
  // ================================================================
  const summaryCards = useMemo(() => {
    if (filteredCourses.length === 0) return [];
    return [
      { label: "Cursos", value: filteredCourses.length },
      { label: "Vagas Oferecidas", value: filteredCourses.reduce((s, c) => s + safeNum(c.subscriptionsLimit), 0) },
      { label: "Inscritos", value: filteredCourses.reduce((s, c) => s + safeNum(c.subscriptions), 0) },
      { label: "Concluintes", value: filteredCourses.reduce((s, c) => s + safeNum(c.graduates), 0) },
    ];
  }, [filteredCourses]);

  // ================================================================
  // CHART BUILDING — depends on drill level
  // ================================================================
  const chartConfigs = useMemo(() => {
    const charts = [];
    if (!statistics) return charts;

    try {
      // ----- LEVEL: OVERVIEW – Grouped by courseType ---- 
      if (drill.level === "overview") {
        // Aggregate by _courseTypeName
        const typeAgg = aggregateBy(allCourses.filter((c) => c._courseTypeName), (c) => c._courseTypeName);

        if (typeAgg.length > 0) {
          const names = typeAgg.map((t) => t.key);

          charts.push({
            id: "bar-courseType",
            type: "grouped-bar",
            title: "Vagas, Inscritos e Concluintes por Tipo de Curso (clique para detalhar)",
            categories: names,
            series: [
              { name: "Vagas Oferecidas", data: typeAgg.map((t) => t.subscriptionsLimit), color: "#4A90D9" },
              { name: "Inscritos", data: typeAgg.map((t) => t.subscriptions), color: "#50C878" },
              { name: "Concluintes", data: typeAgg.map((t) => t.graduates), color: "#FF6B6B" },
            ],
            clickable: true,
            drillTarget: "courseType",
          });

          charts.push({
            id: "pie-courseType-subscriptions",
            type: "pie",
            title: "Distribuição de Inscritos por Tipo de Curso (clique para detalhar)",
            data: typeAgg.map((t) => ({ name: t.key, value: t.subscriptions })),
            clickable: true,
            drillTarget: "courseType",
          });

          charts.push({
            id: "pie-courseType-graduates",
            type: "pie",
            title: "Distribuição de Concluintes por Tipo de Curso",
            data: typeAgg.map((t) => ({ name: t.key, value: t.graduates })),
          });

          charts.push({
            id: "bar-courseType-count",
            type: "bar",
            title: "Quantidade de Cursos por Tipo de Curso (clique para detalhar)",
            categories: names,
            values: typeAgg.map((t) => t.count),
            valueName: "Cursos",
            clickable: true,
            drillTarget: "courseType",
          });
        }

        // Also show Top courses overview
        if (allCourses.length > 0) {
          const sorted = [...allCourses].sort((a, b) => safeNum(b.subscriptions) - safeNum(a.subscriptions));
          const top = sorted.slice(0, 15);
          const labels = top.map((c) => truncLabel((c.title || "").trim(), 35));

          charts.push({
            id: "bar-top-subscriptions",
            type: "bar",
            title: "Top 15 Cursos com Mais Inscritos",
            categories: labels,
            values: top.map((c) => safeNum(c.subscriptions)),
            valueName: "Inscritos",
          });
        }

        // Global profiles & grades
        const profileMap = mergeProfiles(allCourses);
        const pNames = Object.keys(profileMap);
        if (pNames.length > 0) {
          charts.push({
            id: "bar-profile",
            type: "grouped-bar",
            title: "Inscritos e Concluintes por Cargo/Função",
            categories: pNames,
            series: [
              { name: "Inscritos", data: pNames.map((k) => profileMap[k].subscriptions), color: "#50C878" },
              { name: "Concluintes", data: pNames.map((k) => profileMap[k].graduates), color: "#FF6B6B" },
            ],
          });
        }

        const gradeMap = mergeGrades(allCourses);
        const gNames = Object.keys(gradeMap);
        if (gNames.length > 0) {
          charts.push({
            id: "bar-grade",
            type: "grouped-bar",
            title: "Inscritos e Concluintes por Ano/Série",
            categories: gNames,
            series: [
              { name: "Inscritos", data: gNames.map((k) => gradeMap[k].subscriptions), color: "#50C878" },
              { name: "Concluintes", data: gNames.map((k) => gradeMap[k].graduates), color: "#FF6B6B" },
            ],
          });
        }
      }

      // ----- LEVEL: COURSE TYPE – Show segments within this courseType ----
      if (drill.level === "courseType") {
        const segCourses = filteredCourses.filter((c) => c._segmentName);
        const directCourses = filteredCourses.filter((c) => !c._segmentName);

        if (segCourses.length > 0) {
          const segAgg = aggregateBy(segCourses, (c) => c._segmentName);
          const segNames = segAgg.map((s) => s.key);

          charts.push({
            id: "bar-segment-detail",
            type: "grouped-bar",
            title: `Segmentos de "${drill.courseTypeName}" - Vagas, Inscritos e Concluintes (clique para detalhar)`,
            categories: segNames,
            series: [
              { name: "Vagas Oferecidas", data: segAgg.map((s) => s.subscriptionsLimit), color: "#4A90D9" },
              { name: "Inscritos", data: segAgg.map((s) => s.subscriptions), color: "#50C878" },
              { name: "Concluintes", data: segAgg.map((s) => s.graduates), color: "#FF6B6B" },
            ],
            clickable: true,
            drillTarget: "segment",
          });

          charts.push({
            id: "pie-segment-detail-subscriptions",
            type: "pie",
            title: `Inscritos por Segmento - "${drill.courseTypeName}" (clique para detalhar)`,
            data: segAgg.map((s) => ({ name: s.key, value: s.subscriptions })),
            clickable: true,
            drillTarget: "segment",
          });

          charts.push({
            id: "bar-segment-detail-count",
            type: "bar",
            title: `Cursos por Segmento - "${drill.courseTypeName}"`,
            categories: segNames,
            values: segAgg.map((s) => s.count),
            valueName: "Cursos",
          });
        }

        // Direct courses (without segment)
        if (directCourses.length > 0) {
          const labels = directCourses.map((c) => truncLabel((c.title || "").trim(), 35));
          charts.push({
            id: "bar-direct-courses",
            type: "grouped-bar",
            title: `Cursos sem Segmento - "${drill.courseTypeName}"`,
            categories: labels,
            series: [
              { name: "Vagas Oferecidas", data: directCourses.map((c) => safeNum(c.subscriptionsLimit)), color: "#4A90D9" },
              { name: "Inscritos", data: directCourses.map((c) => safeNum(c.subscriptions)), color: "#50C878" },
              { name: "Concluintes", data: directCourses.map((c) => safeNum(c.graduates)), color: "#FF6B6B" },
            ],
          });
        }

        // Profiles & grades for this courseType
        const profileMap = mergeProfiles(filteredCourses);
        const pNames = Object.keys(profileMap);
        if (pNames.length > 0) {
          charts.push({
            id: "bar-profile-courseType",
            type: "grouped-bar",
            title: `Cargo/Função - "${drill.courseTypeName}"`,
            categories: pNames,
            series: [
              { name: "Inscritos", data: pNames.map((k) => profileMap[k].subscriptions), color: "#50C878" },
              { name: "Concluintes", data: pNames.map((k) => profileMap[k].graduates), color: "#FF6B6B" },
            ],
          });
        }

        const gradeMap = mergeGrades(filteredCourses);
        const gNames = Object.keys(gradeMap);
        if (gNames.length > 0) {
          charts.push({
            id: "bar-grade-courseType",
            type: "grouped-bar",
            title: `Ano/Série - "${drill.courseTypeName}"`,
            categories: gNames,
            series: [
              { name: "Inscritos", data: gNames.map((k) => gradeMap[k].subscriptions), color: "#50C878" },
              { name: "Concluintes", data: gNames.map((k) => gradeMap[k].graduates), color: "#FF6B6B" },
            ],
          });
        }
      }

      // ----- LEVEL: SEGMENT – Show courses within this segment ----
      if (drill.level === "segment") {
        if (filteredCourses.length > 0) {
          const labels = filteredCourses.map((c) => truncLabel((c.title || "").trim(), 35));

          charts.push({
            id: "bar-courses-segment",
            type: "grouped-bar",
            title: `Cursos em "${drill.segmentName}"`,
            categories: labels,
            series: [
              { name: "Vagas Oferecidas", data: filteredCourses.map((c) => safeNum(c.subscriptionsLimit)), color: "#4A90D9" },
              { name: "Inscritos", data: filteredCourses.map((c) => safeNum(c.subscriptions)), color: "#50C878" },
              { name: "Concluintes", data: filteredCourses.map((c) => safeNum(c.graduates)), color: "#FF6B6B" },
            ],
          });

          charts.push({
            id: "pie-courses-segment-subscriptions",
            type: "pie",
            title: `Inscritos por Curso - "${drill.segmentName}"`,
            data: filteredCourses.map((c) => ({
              name: truncLabel((c.title || "").trim(), 30),
              value: safeNum(c.subscriptions),
            })),
          });

          charts.push({
            id: "pie-courses-segment-graduates",
            type: "pie",
            title: `Concluintes por Curso - "${drill.segmentName}"`,
            data: filteredCourses.map((c) => ({
              name: truncLabel((c.title || "").trim(), 30),
              value: safeNum(c.graduates),
            })),
          });
        }

        // Profiles & grades for this segment
        const profileMap = mergeProfiles(filteredCourses);
        const pNames = Object.keys(profileMap);
        if (pNames.length > 0) {
          charts.push({
            id: "bar-profile-segment",
            type: "grouped-bar",
            title: `Cargo/Função - "${drill.segmentName}"`,
            categories: pNames,
            series: [
              { name: "Inscritos", data: pNames.map((k) => profileMap[k].subscriptions), color: "#50C878" },
              { name: "Concluintes", data: pNames.map((k) => profileMap[k].graduates), color: "#FF6B6B" },
            ],
          });
        }

        const gradeMap = mergeGrades(filteredCourses);
        const gNames = Object.keys(gradeMap);
        if (gNames.length > 0) {
          charts.push({
            id: "bar-grade-segment",
            type: "grouped-bar",
            title: `Ano/Série - "${drill.segmentName}"`,
            categories: gNames,
            series: [
              { name: "Inscritos", data: gNames.map((k) => gradeMap[k].subscriptions), color: "#50C878" },
              { name: "Concluintes", data: gNames.map((k) => gradeMap[k].graduates), color: "#FF6B6B" },
            ],
          });
        }
      }
    } catch (err) {
      console.error("Error building chart configs:", err);
    }

    return charts;
  }, [statistics, allCourses, filteredCourses, drill]);

  // ================================================================
  // Chart click handler — drill down
  // ================================================================
  const handleChartClick = useCallback(
    (chartConfig, params) => {
      if (!chartConfig.clickable) return;

      const name = params.name || params.value?.name || "";
      if (!name) return;

      if (chartConfig.drillTarget === "courseType") {
        // Find the courseType in raw data
        const matched = courseTypesRaw.find((ct) => ct.courseTypeName === name || ct.courseType === name);
        if (matched) {
          setDrill((prev) => ({
            level: "courseType",
            courseTypeName: matched.courseTypeName,
            courseTypeId: matched.courseTypeId,
            segmentName: null,
            segmentId: null,
            history: [...prev.history, { level: "overview", courseTypeName: null, courseTypeId: null, segmentName: null, segmentId: null }],
          }));
        }
      } else if (chartConfig.drillTarget === "segment") {
        setDrill((prev) => ({
          level: "segment",
          courseTypeName: prev.courseTypeName,
          courseTypeId: prev.courseTypeId,
          segmentName: name,
          segmentId: null,
          history: [...prev.history, { level: "courseType", courseTypeName: prev.courseTypeName, courseTypeId: prev.courseTypeId, segmentName: null, segmentId: null }],
        }));
      }
    },
    [courseTypesRaw]
  );

  // ================================================================
  // Navigation
  // ================================================================
  const goBack = useCallback(() => {
    setDrill((prev) => {
      if (prev.history.length === 0) return prev;
      const previous = prev.history[prev.history.length - 1];
      return {
        ...previous,
        history: prev.history.slice(0, -1),
      };
    });
  }, []);

  const goToOverview = useCallback(() => {
    setDrill({ level: "overview", courseTypeName: null, courseTypeId: null, segmentName: null, segmentId: null, history: [] });
  }, []);

  // ================================================================
  // Build ECharts option
  // ================================================================
  const buildChartOption = useCallback((chartConfig) => {
    const baseOption = {
      tooltip: { trigger: chartConfig.type === "pie" ? "item" : "axis" },
      toolbox: {
        show: true,
        feature: {
          saveAsImage: { title: "Salvar" },
          dataView: { title: "Dados", lang: ["Dados", "Fechar", ""] },
          restore: { title: "Restaurar" },
        },
      },
      color: COLORS,
    };

    if (chartConfig.type === "pie") {
      return {
        ...baseOption,
        tooltip: {
          trigger: "item",
          formatter: (params) => `${params.name}<br/>${params.value} (${params.percent}%)`,
        },
        series: [
          {
            type: "pie",
            radius: ["0%", "70%"],
            center: ["50%", "55%"],
            data: chartConfig.data,
            label: { show: true, formatter: "{b}: {c}", fontSize: 11 },
            emphasis: {
              label: { show: true, fontSize: 14, fontWeight: "bold" },
              itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: "rgba(0, 0, 0, 0.5)" },
            },
          },
        ],
      };
    }

    if (chartConfig.type === "grouped-bar") {
      const { categories, series } = chartConfig;
      return {
        ...baseOption,
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
          formatter: (params) => {
            if (Array.isArray(params)) {
              const title = params[0]?.axisValue || "";
              const lines = params.map((p) => `${p.marker} ${p.seriesName}: ${p.value}`);
              return `<strong>${title}</strong><br/>${lines.join("<br/>")}`;
            }
            return "";
          },
        },
        legend: { data: series.map((s) => s.name), top: "bottom", left: "center" },
        grid: { left: "3%", right: "4%", bottom: "20%", containLabel: true },
        xAxis: {
          type: "category",
          data: categories,
          axisLabel: {
            interval: 0,
            rotate: categories.length > 6 ? 45 : 0,
            fontSize: 10,
            formatter: (value) => truncLabel(value, 22),
          },
        },
        yAxis: { type: "value" },
        series: series.map((s) => ({
          type: "bar",
          name: s.name,
          data: s.data,
          itemStyle: { color: s.color, borderRadius: [4, 4, 0, 0] },
          emphasis: { itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: "rgba(0, 0, 0, 0.3)" } },
        })),
      };
    }

    // Simple bar
    const { categories, values, valueName } = chartConfig;
    return {
      ...baseOption,
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params) => {
          if (Array.isArray(params)) {
            const title = params[0]?.axisValue || "";
            const lines = params.map((p) => `${p.marker} ${p.seriesName || valueName || ""}: ${p.value}`);
            return `<strong>${title}</strong><br/>${lines.join("<br/>")}`;
          }
          return "";
        },
      },
      grid: { left: "3%", right: "4%", bottom: "20%", containLabel: true },
      xAxis: {
        type: "category",
        data: categories || [],
        axisLabel: {
          interval: 0,
          rotate: (categories || []).length > 6 ? 45 : 0,
          fontSize: 10,
          formatter: (value) => truncLabel(value),
        },
      },
      yAxis: { type: "value" },
      series: [
        {
          type: "bar",
          name: valueName || "Valor",
          data: values || [],
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: "#4A90D9" },
              { offset: 1, color: "#2C5F8A" },
            ]),
            borderRadius: [4, 4, 0, 0],
          },
          emphasis: { itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: "rgba(0, 0, 0, 0.3)" } },
        },
      ],
    };
  }, []);

  const toggleFullscreen = (chartId) => {
    setFullscreenChart(fullscreenChart === chartId ? null : chartId);
  };

  const renderChart = (chartConfig) => {
    const isFullscreen = fullscreenChart === chartConfig.id;
    const option = buildChartOption(chartConfig);

    const onEvents = {};
    if (chartConfig.clickable) {
      onEvents.click = (params) => handleChartClick(chartConfig, params);
    }

    return (
      <ChartCard key={chartConfig.id} fullscreen={isFullscreen}>
        <ChartTitle>{chartConfig.title}</ChartTitle>
        <ChartWrapper height={isFullscreen ? "80vh" : "400px"}>
          <ReactEcharts
            option={option}
            style={{ width: "100%", height: "100%" }}
            opts={{ renderer: "canvas" }}
            onEvents={onEvents}
          />
        </ChartWrapper>
        <ButtonGroup>
          <ReadMoreButton
            onClick={() => toggleFullscreen(chartConfig.id)}
            text={isFullscreen ? "Sair da Tela Cheia" : "Tela Cheia"}
          />
        </ButtonGroup>
      </ChartCard>
    );
  };

  // ================================================================
  // RENDER
  // ================================================================
  const renderBreadcrumb = () => {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          flexWrap: "wrap",
          marginBottom: "1rem",
          padding: "0.5rem 1rem",
          background: "var(--softGray, #f0f0f0)",
          borderRadius: "0.5rem",
          width: "100%",
        }}
      >
        <ReadMoreButton onClick={goToOverview} text="Visão Geral" />
        {drill.level === "courseType" || drill.level === "segment" ? (
          <>
<span style={{ fontSize: "1.2rem", color: "var(--primary, #333)" }}>{'>'}</span>
            <span style={{ fontWeight: "bold", fontSize: "0.9rem", color: "var(--darkBlue, #2C5F8A)" }}>
              {drill.courseTypeName}
            </span>
          </>
        ) : null}
        {drill.level === "segment" ? (
          <>
            <span style={{ fontSize: "1.2rem", color: "var(--primary, #333)" }}>{'>'}</span>
            <span style={{ fontWeight: "bold", fontSize: "0.9rem", color: "var(--darkBlue, #2C5F8A)" }}>
              {drill.segmentName}
            </span>
          </>
        ) : null}
        {drill.history.length > 0 && (
          <ReadMoreButton onClick={goBack} text="← Voltar" />
        )}
      </div>
    );
  };

  return (
    <Container>
      {loadDashboardInfo.loading && <FullScreenLoading />}

      <Menu />
      <Banner icon={images.iconAvaliacao}>DASHBOARD DE CURSOS</Banner>

      <ContentBox>
        <FilterBar>
          <YearSelector>
            <span>Selecione o ano:</span>
            <select value={selectedYear} onChange={handleYearChange}>
              {AVAILABLE_YEARS.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </YearSelector>
        </FilterBar>

        {/* Breadcrumb / Navigation */}
        {dashboardData && !loadDashboardInfo.loading && renderBreadcrumb()}

        {loadDashboardInfo.error && (
          <ErrorMessage>
            <p>Erro ao carregar dados do dashboard: {loadDashboardInfo.error}</p>
            <ReadMoreButton
              onClick={() => loadDashboard({ params: { year: selectedYear } })}
              text="Tentar novamente"
            />
          </ErrorMessage>
        )}

        {summaryCards.length > 0 && (
          <SummaryRow>
            {summaryCards.map((card, idx) => (
              <SummaryCard key={idx}>
                <div className="value">{card.value}</div>
                <div className="label">{card.label}</div>
              </SummaryCard>
            ))}
          </SummaryRow>
        )}

        {chartConfigs.length > 0 && (
          <ChartsGrid>
            {chartConfigs.map((chartConfig) => renderChart(chartConfig))}
          </ChartsGrid>
        )}

        {!loadDashboardInfo.loading && !loadDashboardInfo.error && chartConfigs.length === 0 && dashboardData && (
          <EmptyState>
            <p>Nenhum dado disponível para gerar gráficos.</p>
            <pre style={{ textAlign: "left", maxWidth: "100%", overflow: "auto", background: "#f5f5f5", padding: "1rem", borderRadius: "0.5rem", fontSize: "0.75rem" }}>
              {JSON.stringify(dashboardData, null, 2)}
            </pre>
          </EmptyState>
        )}

        {!dashboardData && !loadDashboardInfo.loading && !loadDashboardInfo.error && (
          <EmptyState>
            <p>Selecione um ano para carregar os dados do dashboard.</p>
          </EmptyState>
        )}

        {dashboardData && process.env.NODE_ENV === "development" && (
          <details style={{ marginTop: "2rem", width: "100%" }}>
            <summary style={{ cursor: "pointer", fontWeight: "bold" }}>Dados Brutos da API</summary>
            <pre style={{ maxWidth: "100%", overflow: "auto", background: "#f5f5f5", padding: "1rem", borderRadius: "0.5rem", fontSize: "0.7rem", marginTop: "0.5rem" }}>
              {JSON.stringify(dashboardData, null, 2)}
            </pre>
          </details>
        )}
      </ContentBox>

      <Footer />
    </Container>
  );
};

export default CourseDashboard;

